import { useState, useEffect, useRef } from "react";

import { Check, Home, MapPin, Ruler, BedDouble, Bath, Sparkles, User, Mail, Phone, Building } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08
    }
  }
};

interface BookingFormProps {
  onProgressChange?: (progress: number) => void;
}

export default function BookingForm({ onProgressChange }: BookingFormProps) {
  const { t } = useLanguage();
  
  const { toast } = useToast();
  const [propertyType, setPropertyType] = useState("");
  const [location, setLocation] = useState("");
  const [surface, setSurface] = useState("");
  const [bedrooms, setBedrooms] = useState("3");
  const [bathrooms, setBathrooms] = useState("2");
  const [amenities, setAmenities] = useState<string[]>([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);

  const calculateProgress = () => {
    let filledFields = 0;
    const totalFields = 10;
    
    if (propertyType) filledFields++;
    if (location) filledFields++;
    if (surface) filledFields++;
    if (bedrooms) filledFields++;
    if (bathrooms) filledFields++;
    if (address) filledFields++;
    if (firstName) filledFields++;
    if (lastName) filledFields++;
    if (email) filledFields++;
    if (phone) filledFields++;
    
    return (filledFields / totalFields) * 100;
  };

  const progress = calculateProgress();

  // Notify parent of progress changes
  useEffect(() => {
    onProgressChange?.(progress);
  }, [progress, onProgressChange]);

  const handleAmenityToggle = (amenity: string) => {
    setAmenities(prev => 
      prev.includes(amenity) 
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!propertyType || !location || !surface || !firstName || !lastName || !email || !phone || !address) {
      toast({
        title: "Champs requis",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive"
      });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Email invalide",
        description: "Veuillez entrer une adresse email valide.",
        variant: "destructive"
      });
      return;
    }

    if (!captchaToken) {
      toast({
        title: "Captcha requis",
        description: "Veuillez compléter le captcha.",
        variant: "destructive"
      });
      return;
    }
    
    setSubmitted(true);
    
    try {
      // Get translated labels for property type and location
      const propertyTypeLabels: Record<string, string> = {
        villa: t.bookingForm.villa,
        apartment: t.bookingForm.apartment,
        house: t.bookingForm.house,
        penthouse: t.bookingForm.penthouse
      };
      
      const locationLabels: Record<string, string> = {
        cannes: t.bookingForm.cannes,
        nice: t.bookingForm.nice,
        "saint-tropez": t.bookingForm.saintTropez,
        antibes: t.bookingForm.antibes,
        monaco: t.bookingForm.monaco,
        marseille: t.bookingForm.marseille,
        aix: t.bookingForm.aixEnProvence,
        other: t.bookingForm.other
      };

      const amenityLabels: Record<string, string> = {
        pool: t.bookingForm.pool,
        panoramicView: t.bookingForm.panoramicView,
        waterfront: t.bookingForm.waterfront,
        garden: t.bookingForm.garden,
        parking: t.bookingForm.parking,
        airConditioning: t.bookingForm.airConditioning
      };

      // Send estimation request via Web3Forms
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: "9b248f28-7389-4c29-a486-ab80ea310230",
          cc: "esl@outlook.fr",
          subject: `🏠 Nouvelle demande d'estimation - ${propertyTypeLabels[propertyType] || propertyType} à ${locationLabels[location] || location}`,
          from_name: `${firstName} ${lastName}`,
          name: `${firstName} ${lastName}`,
          email: email,
          phone: phone,
          property_type: propertyTypeLabels[propertyType] || propertyType,
          location: locationLabels[location] || location,
          surface: `${surface} m²`,
          address: address,
          bedrooms: `${bedrooms} chambre(s)`,
          bathrooms: `${bathrooms} salle(s) de bain`,
          amenities: amenities.length > 0 
            ? amenities.map(a => amenityLabels[a] || a).join(", ") 
            : "Aucun équipement sélectionné",
          "h-captcha-response": captchaToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Erreur lors de l'envoi");
      }

      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);
      
      toast({
        title: "Demande envoyée !",
        description: "Nous avons bien reçu votre demande d'estimation. Notre équipe vous contactera sous 24h.",
      });

      // Reset form after successful submission
      setTimeout(() => {
        setPropertyType("");
        setLocation("");
        setSurface("");
        setBedrooms("3");
        setBathrooms("2");
        setAmenities([]);
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setAddress("");
        setSubmitted(false);
      }, 2000);
      
    } catch (error) {
      console.error("Erreur Web3Forms:", error);
      setSubmitted(false);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    }
  };

  const amenitiesList = [
    { id: 'pool', label: t.bookingForm.pool, icon: "🏊" },
    { id: 'panoramicView', label: t.bookingForm.panoramicView, icon: "🌅" },
    { id: 'waterfront', label: t.bookingForm.waterfront, icon: "🌊" },
    { id: 'garden', label: t.bookingForm.garden, icon: "🌳" },
    { id: 'parking', label: t.bookingForm.parking, icon: "🚗" },
    { id: 'airConditioning', label: t.bookingForm.airConditioning, icon: "❄️" }
  ];

  return (
    <motion.form 
      onSubmit={handleSubmit}
      initial="initial"
      animate="animate"
      variants={staggerContainer}
      className="relative bg-card/80 backdrop-blur-sm border border-border/50 p-4 md:p-5 space-y-4"
    >


      {/* Property Details Section */}
      <motion.div variants={fadeInUp} className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-[1px] bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Votre Bien</span>
          <div className="flex-1 h-[1px] bg-border" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {/* Property Type */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label id="property-type-label" className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Building className="w-3.5 h-3.5" />
              {t.bookingForm.propertyType}
            </label>
            <Select value={propertyType} onValueChange={setPropertyType}>
              <SelectTrigger aria-labelledby="property-type-label" className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors">
                <SelectValue placeholder={t.bookingForm.selectPropertyType} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="villa">{t.bookingForm.villa}</SelectItem>
                <SelectItem value="apartment">{t.bookingForm.apartment}</SelectItem>
                <SelectItem value="house">{t.bookingForm.house}</SelectItem>
                <SelectItem value="penthouse">{t.bookingForm.penthouse}</SelectItem>
              </SelectContent>
            </Select>
          </motion.div>
          
          {/* Location */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label id="location-label" className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <MapPin className="w-3.5 h-3.5" />
              {t.bookingForm.location}
            </label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger aria-labelledby="location-label" className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors">
                <SelectValue placeholder={t.bookingForm.selectLocation} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cannes">{t.bookingForm.cannes}</SelectItem>
                <SelectItem value="nice">{t.bookingForm.nice}</SelectItem>
                <SelectItem value="saint-tropez">{t.bookingForm.saintTropez}</SelectItem>
                <SelectItem value="antibes">{t.bookingForm.antibes}</SelectItem>
                <SelectItem value="monaco">{t.bookingForm.monaco}</SelectItem>
                <SelectItem value="marseille">{t.bookingForm.marseille}</SelectItem>
                <SelectItem value="aix">{t.bookingForm.aixEnProvence}</SelectItem>
                <SelectItem value="other">{t.bookingForm.other}</SelectItem>
              </SelectContent>
            </Select>
          </motion.div>
          
          {/* Surface */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Ruler className="w-3.5 h-3.5" />
              {t.bookingForm.surface}
            </label>
            <Input
              type="number"
              placeholder={t.bookingForm.surfacePlaceholder}
              value={surface}
              onChange={(e) => setSurface(e.target.value)}
              min="20"
              max="2000"
              className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors"
            />
          </motion.div>

          {/* Address */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <MapPin className="w-3.5 h-3.5" />
              {t.bookingForm.address} *
            </label>
            <Input
              type="text"
              placeholder={t.bookingForm.addressPlaceholder}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors"
            />
          </motion.div>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mt-2">
          {/* Bedrooms */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label id="bedrooms-label" className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <BedDouble className="w-3.5 h-3.5" />
              {t.bookingForm.bedrooms}
            </label>
            <Select value={bedrooms} onValueChange={setBedrooms}>
              <SelectTrigger aria-labelledby="bedrooms-label" className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                  <SelectItem key={num} value={num.toString()}>
                    {num} {num === 1 ? 'chambre' : 'chambres'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </motion.div>
          
          {/* Bathrooms */}
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label id="bathrooms-label" className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Bath className="w-3.5 h-3.5" />
              {t.bookingForm.bathrooms}
            </label>
            <Select value={bathrooms} onValueChange={setBathrooms}>
              <SelectTrigger aria-labelledby="bathrooms-label" className="h-9 text-sm bg-background/50 border-border/50 focus:border-foreground transition-colors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <SelectItem key={num} value={num.toString()}>
                    {num} {num === 1 ? 'salle de bain' : 'salles de bain'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </motion.div>
        </div>
        
        {/* Amenities */}
        <motion.div variants={fadeInUp} className="space-y-2">
          <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
            <Sparkles className="w-3.5 h-3.5" />
            {t.bookingForm.amenities}
          </label>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
            {amenitiesList.map((amenity, index) => (
              <motion.div 
                key={amenity.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleAmenityToggle(amenity.id)}
                className={`
                  flex items-center justify-center gap-1.5 py-2 px-2.5 cursor-pointer border transition-all duration-300
                  ${amenities.includes(amenity.id) 
                    ? 'border-foreground bg-foreground/5' 
                    : 'border-border/50 hover:border-foreground/30 bg-background/30'
                  }
                `}
              >
                <span className="text-sm">{amenity.icon}</span>
                <span className="text-xs font-medium truncate">{amenity.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Contact Section */}
      <motion.div variants={fadeInUp} className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-[1px] bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Vos Coordonnées</span>
          <div className="flex-1 h-[1px] bg-border" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <User className="w-3.5 h-3.5" />
              {t.bookingForm.firstName} *
            </label>
            <Input
              type="text"
              placeholder={t.bookingForm.firstNamePlaceholder}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onFocus={() => setFocusedField('firstName')}
              onBlur={() => setFocusedField(null)}
              className={`h-9 text-sm bg-background/50 border-border/50 transition-all duration-300 ${
                focusedField === 'firstName' ? 'border-foreground shadow-sm' : ''
              }`}
            />
          </motion.div>
          
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <User className="w-3.5 h-3.5" />
              {t.bookingForm.lastName} *
            </label>
            <Input
              type="text"
              placeholder={t.bookingForm.lastNamePlaceholder}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onFocus={() => setFocusedField('lastName')}
              onBlur={() => setFocusedField(null)}
              className={`h-9 text-sm bg-background/50 border-border/50 transition-all duration-300 ${
                focusedField === 'lastName' ? 'border-foreground shadow-sm' : ''
              }`}
            />
          </motion.div>

          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Mail className="w-3.5 h-3.5" />
              {t.bookingForm.email} *
            </label>
            <Input
              type="email"
              placeholder={t.bookingForm.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              className={`h-9 text-sm bg-background/50 border-border/50 transition-all duration-300 ${
                focusedField === 'email' ? 'border-foreground shadow-sm' : ''
              }`}
            />
          </motion.div>
          
          <motion.div variants={fadeInUp} className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80">
              <Phone className="w-3.5 h-3.5" />
              {t.bookingForm.phone} *
            </label>
            <Input
              type="tel"
              placeholder={t.bookingForm.phonePlaceholder}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onFocus={() => setFocusedField('phone')}
              onBlur={() => setFocusedField(null)}
              className={`h-9 text-sm bg-background/50 border-border/50 transition-all duration-300 ${
                focusedField === 'phone' ? 'border-foreground shadow-sm' : ''
              }`}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* hCaptcha */}
      <motion.div variants={fadeInUp} className="flex justify-center transform scale-[0.85] origin-center -my-2">
        <HCaptcha
          ref={captchaRef}
          sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
          reCaptchaCompat={false}
          size="normal"
          theme="dark"
          onVerify={(token) => setCaptchaToken(token)}
          onExpire={() => setCaptchaToken(null)}
        />
      </motion.div>

      {/* Submit Button */}
      <motion.div variants={fadeInUp}>
        <Button 
          type="submit" 
          disabled={submitted || !captchaToken}
          className="w-full h-10 relative overflow-hidden bg-foreground text-background hover:bg-foreground/90 transition-all duration-500 group"
        >
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.span
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex items-center justify-center gap-2"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                >
                  <Check className="w-4 h-4" />
                </motion.div>
                <span className="tracking-wider text-sm">{t.bookingForm.bookingConfirmed}</span>
              </motion.span>
            ) : (
              <motion.span
                key="submit"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex items-center justify-center gap-2"
              >
                <Home className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                <span className="tracking-wider text-xs uppercase">{t.bookingForm.checkAvailability}</span>
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </motion.div>

      {/* Trust indicators */}
      <motion.div 
        variants={fadeInUp}
        className="flex items-center justify-center gap-4 pt-3 border-t border-border/30"
      >
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Check className="w-3 h-3" />
          <span>Estimation gratuite</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Check className="w-3 h-3" />
          <span>Réponse sous 24h</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Check className="w-3 h-3" />
          <span>Sans engagement</span>
        </div>
      </motion.div>
    </motion.form>
  );
}
